import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Upload, Image as ImageIcon, Download, Trash2, Loader2, ArrowLeft,
  Calendar, Settings, Sparkles, Check, Copy, Share2, RefreshCw
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITION_OPTIONS = [
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'center', label: 'Center' }
];

const PhotoEnhancementPage = () => {
  const navigate = useNavigate();
  const { communityId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  const [community, setCommunity] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Enhancement settings
  const [enhanceSettings, setEnhanceSettings] = useState({
    add_logo: true,
    add_date: true,
    enhance_image: true,
    service_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    title: '',
    logo_position: 'bottom-right',
    date_position: 'bottom-left',
    logo_size: 150,
    logo_opacity: 0.9
  });
  
  // Dialog states
  const [enhanceDialogOpen, setEnhanceDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);

  const fetchCommunity = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/communities/${communityId}`, { withCredentials: true });
      setCommunity(response.data);
    } catch (error) {
      console.error('Failed to fetch community:', error);
      toast.error('Failed to load community');
      navigate('/community');
    }
  }, [communityId, navigate]);

  const fetchPhotos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/communities/${communityId}/photos`, { withCredentials: true });
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    fetchCommunity();
    fetchPhotos();
  }, [fetchCommunity, fetchPhotos]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 10MB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/communities/${communityId}/photos`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success('Photo uploaded successfully');
      fetchPhotos();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(getErrorMessage(error, 'Failed to upload photo'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await axios.put(
            `${API}/communities/${communityId}/logo`,
            { logo_data: reader.result },
            { withCredentials: true }
          );
          toast.success('Logo updated successfully');
          fetchCommunity();
          setLogoDialogOpen(false);
        } catch (error) {
          console.error('Logo upload failed:', error);
          toast.error(getErrorMessage(error, 'Failed to upload logo'));
        } finally {
          setUploadingLogo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadingLogo(false);
      toast.error('Failed to read file');
    }
    
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleEnhancePhoto = async () => {
    if (!selectedPhoto) return;
    
    setEnhancing(true);
    
    try {
      const response = await axios.post(
        `${API}/communities/${communityId}/photos/${selectedPhoto.photo_id}/enhance`,
        enhanceSettings,
        { withCredentials: true }
      );
      
      toast.success('Photo enhanced successfully!');
      setSelectedPhoto({ ...selectedPhoto, ...response.data, enhanced_url: response.data.enhanced_url });
      setEnhanceDialogOpen(false);
      fetchPhotos();
    } catch (error) {
      console.error('Enhancement failed:', error);
      toast.error(getErrorMessage(error, 'Failed to enhance photo'));
    } finally {
      setEnhancing(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/communities/${communityId}/photos/${photoId}`, { withCredentials: true });
      toast.success('Photo deleted');
      setPhotos(photos.filter(p => p.photo_id !== photoId));
      if (selectedPhoto?.photo_id === photoId) {
        setSelectedPhoto(null);
        setPreviewDialogOpen(false);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete photo');
    }
  };

  const downloadImage = (imageUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename || 'enhanced-photo.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openEnhanceDialog = (photo) => {
    setSelectedPhoto(photo);
    setEnhanceSettings(prev => ({
      ...prev,
      service_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      title: community?.name || ''
    }));
    setEnhanceDialogOpen(true);
  };

  const openPreview = (photo) => {
    setSelectedPhoto(photo);
    setPreviewDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const isAdmin = community?.my_role === 'admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-900/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/community')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Photo Enhancement</h1>
              <p className="text-sm text-slate-400">{community?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setLogoDialogOpen(true)}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <Settings className="w-4 h-4 mr-2" />
                {community?.logo_url ? 'Change' : 'Add'} Logo
              </Button>
            )}
            
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Photo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Logo Status Banner */}
        {!community?.logo_url && isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30"
          >
            <div className="flex items-center gap-3">
              <ImageIcon className="w-5 h-5 text-amber-500" />
              <div className="flex-1">
                <p className="text-white font-medium">Add your church logo</p>
                <p className="text-sm text-slate-400">Upload a logo to overlay on your service photos</p>
              </div>
              <Button
                size="sm"
                onClick={() => setLogoDialogOpen(true)}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                Upload Logo
              </Button>
            </div>
          </motion.div>
        )}

        {/* Photos Grid */}
        {photos.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-16 text-center">
              <ImageIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No photos yet</h3>
              <p className="text-slate-400 mb-6">Upload service photos to enhance them with your church logo and date</p>
              <Button 
                onClick={() => fileInputRef.current?.click()}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Photo
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <AnimatePresence>
              {photos.map((photo, index) => (
                <motion.div
                  key={photo.photo_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative aspect-video rounded-lg overflow-hidden bg-slate-800 cursor-pointer"
                  onClick={() => openPreview(photo)}
                  data-testid={`photo-card-${photo.photo_id}`}
                >
                  <img
                    src={photo.enhanced_url || photo.original_url}
                    alt="Service photo"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Status badges */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {photo.is_enhanced && (
                      <Badge className="bg-emerald-500/80 text-white text-xs">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Enhanced
                      </Badge>
                    )}
                    {photo.has_logo && (
                      <Badge className="bg-amber-500/80 text-black text-xs">Logo</Badge>
                    )}
                    {photo.has_date && (
                      <Badge className="bg-blue-500/80 text-white text-xs">Date</Badge>
                    )}
                  </div>
                  
                  {/* Action buttons on hover */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEnhanceDialog(photo);
                      }}
                      className="bg-white/20 hover:bg-white/30 text-white"
                      data-testid={`enhance-btn-${photo.photo_id}`}
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      Enhance
                    </Button>
                    
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadImage(photo.enhanced_url || photo.original_url, `photo-${photo.photo_id}.jpg`);
                        }}
                        className="bg-white/20 hover:bg-white/30 text-white w-8 h-8"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePhoto(photo.photo_id);
                        }}
                        className="bg-red-500/20 hover:bg-red-500/40 text-red-400 w-8 h-8"
                        data-testid={`delete-btn-${photo.photo_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Enhance Dialog */}
      <Dialog open={enhanceDialogOpen} onOpenChange={setEnhanceDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Enhance Photo</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add your church logo, service date, and apply AI enhancements
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Preview */}
            <div className="aspect-video rounded-lg overflow-hidden bg-slate-800">
              {selectedPhoto && (
                <img
                  src={selectedPhoto.original_url}
                  alt="Photo to enhance"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            
            {/* Settings */}
            <div className="space-y-4">
              {/* Add Logo */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-slate-400" />
                  <Label className="text-white">Add Logo</Label>
                </div>
                <Switch
                  checked={enhanceSettings.add_logo}
                  onCheckedChange={(checked) => setEnhanceSettings(prev => ({ ...prev, add_logo: checked }))}
                  disabled={!community?.logo_url}
                />
              </div>
              {!community?.logo_url && (
                <p className="text-xs text-amber-500">Upload a logo first to enable this option</p>
              )}
              
              {enhanceSettings.add_logo && community?.logo_url && (
                <div className="pl-6 space-y-3">
                  <div>
                    <Label className="text-slate-400 text-sm">Position</Label>
                    <Select
                      value={enhanceSettings.logo_position}
                      onValueChange={(value) => setEnhanceSettings(prev => ({ ...prev, logo_position: value }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {POSITION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} className="text-white">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-sm">Size: {enhanceSettings.logo_size}px</Label>
                    <Slider
                      value={[enhanceSettings.logo_size]}
                      onValueChange={([value]) => setEnhanceSettings(prev => ({ ...prev, logo_size: value }))}
                      min={50}
                      max={300}
                      step={10}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-sm">Opacity: {Math.round(enhanceSettings.logo_opacity * 100)}%</Label>
                    <Slider
                      value={[enhanceSettings.logo_opacity]}
                      onValueChange={([value]) => setEnhanceSettings(prev => ({ ...prev, logo_opacity: value }))}
                      min={0.3}
                      max={1}
                      step={0.1}
                      className="mt-2"
                    />
                  </div>
                </div>
              )}
              
              {/* Add Date */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <Label className="text-white">Add Date</Label>
                </div>
                <Switch
                  checked={enhanceSettings.add_date}
                  onCheckedChange={(checked) => setEnhanceSettings(prev => ({ ...prev, add_date: checked }))}
                />
              </div>
              
              {enhanceSettings.add_date && (
                <div className="pl-6 space-y-3">
                  <div>
                    <Label className="text-slate-400 text-sm">Service Date</Label>
                    <Input
                      value={enhanceSettings.service_date}
                      onChange={(e) => setEnhanceSettings(prev => ({ ...prev, service_date: e.target.value }))}
                      placeholder="January 12, 2026"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-sm">Title (optional)</Label>
                    <Input
                      value={enhanceSettings.title}
                      onChange={(e) => setEnhanceSettings(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Sunday Worship"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-sm">Position</Label>
                    <Select
                      value={enhanceSettings.date_position}
                      onValueChange={(value) => setEnhanceSettings(prev => ({ ...prev, date_position: value }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {POSITION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} className="text-white">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {/* AI Enhancement */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <Label className="text-white">AI Enhancement</Label>
                </div>
                <Switch
                  checked={enhanceSettings.enhance_image}
                  onCheckedChange={(checked) => setEnhanceSettings(prev => ({ ...prev, enhance_image: checked }))}
                />
              </div>
              <p className="text-xs text-slate-500 pl-6">Automatically improve brightness, contrast, and sharpness</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEnhanceDialogOpen(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEnhancePhoto}
              disabled={enhancing || (!enhanceSettings.add_logo && !enhanceSettings.add_date && !enhanceSettings.enhance_image)}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="apply-enhancement-btn"
            >
              {enhancing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Apply Enhancement
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-white">Photo Preview</DialogTitle>
          </DialogHeader>
          
          {selectedPhoto && (
            <div className="space-y-4">
              <div className="aspect-video rounded-lg overflow-hidden bg-slate-800">
                <img
                  src={selectedPhoto.enhanced_url || selectedPhoto.original_url}
                  alt="Photo preview"
                  className="w-full h-full object-contain"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {selectedPhoto.is_enhanced && (
                    <Badge className="bg-emerald-500/20 text-emerald-400">Enhanced</Badge>
                  )}
                  {selectedPhoto.has_logo && (
                    <Badge className="bg-amber-500/20 text-amber-400">With Logo</Badge>
                  )}
                  {selectedPhoto.has_date && (
                    <Badge className="bg-blue-500/20 text-blue-400">With Date</Badge>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPreviewDialogOpen(false);
                      openEnhanceDialog(selectedPhoto);
                    }}
                    className="border-slate-700 text-slate-300"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Re-enhance
                  </Button>
                  <Button
                    onClick={() => downloadImage(selectedPhoto.enhanced_url || selectedPhoto.original_url, `photo-${selectedPhoto.photo_id}.jpg`)}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Logo Upload Dialog */}
      <Dialog open={logoDialogOpen} onOpenChange={setLogoDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Upload Church Logo</DialogTitle>
            <DialogDescription className="text-slate-400">
              Upload your church logo to overlay on service photos. PNG with transparency works best.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {community?.logo_url ? (
              <div className="mb-4 p-4 rounded-lg bg-slate-800 flex items-center justify-center">
                <img
                  src={community.logo_url}
                  alt="Current logo"
                  className="max-h-32 max-w-full object-contain"
                />
              </div>
            ) : null}
            
            <div 
              className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
              onClick={() => logoInputRef.current?.click()}
            >
              {uploadingLogo ? (
                <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto" />
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400">Click to upload logo</p>
                  <p className="text-xs text-slate-500 mt-1">PNG, JPG, or WebP up to 5MB</p>
                </>
              )}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setLogoDialogOpen(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoEnhancementPage;
